/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class RenderSequenceImpl
 * @hideconstructor
 * @classdesc
 */
define([
	'underscore',
	'../../../../lib/@waca/dashboard-common/dist/api/impl/CallbackInvoker',
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../lib/@waca/dashboard-common/dist/api/Error',
	'../api/RenderContextAPI',
	'../s12y/RenderSequenceInfo',
	'../s12y/ServiceabilityRenderingInfo',
	'../s12y/ServiceabilitySQLInfo',
	'./RenderContextImpl',
	'../s12y/RenderingInfo'
], (_,
	CallbackInvoker,
	APIFactory,
	APIError,
	RenderContextAPI,
	RenderSequenceInfo,
	ServiceabilityRenderingInfo,
	ServiceabilitySQLInfo,
	RenderContextImpl,
	RenderingInfo) => {
	class RenderSequenceImpl extends CallbackInvoker {
		static START_TASK_EXECUTION		= 'startTaskExecution';
		static RESOLVE_TASK_EXECUTION 	= 'resolveTaskExecution';
		static REJECT_TASK_EXECUTION 	= 'rejectTaskExecution';

		static LAST_RENDER_WITH_DATA   = 'lastRenderingWithDataRequest';
		static S12Y_RENDERING_INFO = 'ServiceabilityRenderingInfo';
		static S12Y_SQL_INFO = 'ServiceabilitySQLInfo';

		/**
		 * TODO: remove the usage of dashboardAPI.getService when dashboardAPI.getFeature includes the glass service
		 * @param {Object} options
		 * @param {DashboardAPI} options.dashboardAPI
		 * @param {Object[]} options.features
		 */
		constructor({ dashboardAPI, features, content }) {
			let logger = dashboardAPI.getService(dashboardAPI.GLOBAL_SERVICES.LOGGER);
			super({ logger: logger });
			this._dashboard = dashboardAPI;
			this._logger = logger;
			this._s12yAPI = features['Serviceability'];
			this._renderStepProviders = [];
			this._currentRenderContext = {};
			this.content = content;
			let impl = this;
			this.onStartTaskExecution(function(options) { impl.s12yOnStartTaskExecution(options); });
			this.onResolveTaskExecution(function(options) { impl.s12yOnResolveTaskExecution(options); });
			this.onRejectTaskExecution(function(options) { impl.s12yOnRejectTaskExecution(options); });
		}

		registerRenderEngine(renderEngine) {
			this._engine = renderEngine;
		}

		/**
		 * @implements RenderSequenceInternalAPI#triggerRenderSequence
		 */
		triggerRenderSequence(options) {
			this._currentRenderContext = {};

			this._s12yStartRenderSequence(options);
			return this._engine.process(options).then((result) => {
				this._s12yResolveRenderSequence(options);
				return result;
			}).catch((error) => {
				this._s12yResolveRenderSequence(options);
				throw error;
			});
		}

		/**
		 * @private
		 * @function _s12yStartRenderSequence
		 * @description Start tracking the widget render time by creating a current render object
		 * @param {Object} options
		 * @param {number} options.renderId - the current render ID that is being executed
		 */
		_s12yStartRenderSequence(options) {
			try {
				let contentInfo = this._s12yAPI.getContentInfo();
				if(contentInfo) {
					let s12ySQLInfo = contentInfo.getData(RenderSequenceImpl.S12Y_SQL_INFO);
					if (!s12ySQLInfo) {
						//TODO:Seems out of place to access DataQueryExecution inside render sequence
						//see if there is a better way to access this
						//ServiceabilitySQLInfo could be used in other render tasks in theory
						const queryExecution = this.content.getFeature('DataQueryExecution');
						const visApi = this.content.getFeature('WidgetAPI.deprecated').getVisApi();
						s12ySQLInfo = new ServiceabilitySQLInfo(visApi, queryExecution);
						contentInfo.setData(RenderSequenceImpl.S12Y_SQL_INFO, s12ySQLInfo);
					}

					let s12yRenderingInfo = contentInfo.getData(RenderSequenceImpl.S12Y_RENDERING_INFO);
					if (!s12yRenderingInfo) {
						s12yRenderingInfo = new ServiceabilityRenderingInfo(this.content, this._dashboard);
						contentInfo.setData(RenderSequenceImpl.S12Y_RENDERING_INFO, s12yRenderingInfo);
					}

					let renderingInfo = s12yRenderingInfo.getCurrentRendering();

					if(renderingInfo === undefined) {
						renderingInfo = new RenderingInfo({ renderId: options.renderId });
						s12yRenderingInfo.setCurrentRendering(renderingInfo);
					} else {
						renderingInfo.registerSequenceId(options.renderId);
					}
				} else {
					this._logger.info('Serviceability feature is not enabled');
				}
			} catch(error) {
				this._logger.error(`Failed to track RenderingInfo for renderId [${options.renderId}]`, error);
			}
		}

		/**
		 * @private
		 * @function s12yResolveRenderSequence
		 * @description Resolve the tracking for RenderInfo
		 * @param {Object} options
		 * @param {number} options.renderId - the current render ID that is being executed
		 */
		_s12yResolveRenderSequence(options) {
			try {
				let contentInfo = this._s12yAPI.getContentInfo();
				if(contentInfo) {
					let s12yRenderingInfo = contentInfo.getData(RenderSequenceImpl.S12Y_RENDERING_INFO);

					let renderingInfo = s12yRenderingInfo.getCurrentRendering();
					if (renderingInfo && renderingInfo.getLastSequenceId() === options.renderId) {
						renderingInfo.setEndTime(Date.now());
						if (renderingInfo.isWithData()) {
							s12yRenderingInfo.setLastRendering(renderingInfo);
						}
						s12yRenderingInfo.setCurrentRendering(undefined);
					}
				}
			} catch(error) {
				this._logger.error(`Failed to track RenderingInfo for renderId [${options.renderId}]`, error);
			}
		}

		getCurrentRenderContext(taskId) {
			return this._currentRenderContext[taskId];
		}

		/**
		 * TODO: remove the call to _isStepComplete as a task should be processed only when it needs
		 * @implements RenderSequenceInternalAPI#startTaskExecution
		 */
		startTaskExecution(taskInfo) {
			let taskId = taskInfo.task.instance.getId();
			try {
				if (taskInfo.task.instance._isStepComplete(taskInfo.renderContext, taskId) === true) {
					this._logger.info(`execution of task [${taskId}] is ignored for s12y as it is already complete`);
				} else {
					this._setSQLFetchParams(taskInfo.renderContext);
					let taskRenderContext = this._createTaskRenderContext(taskInfo.renderContext);
					this.invokeCallbacks(RenderSequenceImpl.START_TASK_EXECUTION, { renderContext: taskRenderContext, taskId: taskId });
				}
			} catch (error) {
				this._logger.error(`fail to check if task [${taskId}] is complete`, error);
			}

			return taskInfo.task.instance.process(taskInfo.renderContext);
		}

		/**
		 * @implements RenderSequenceInternalAPI#resolveTaskExecution
		 */
		resolveTaskExecution(taskInfo) {
			let taskId = taskInfo.task.instance.getId();
			let taskRenderContext = this._createTaskRenderContext(taskInfo.renderContext);
			this.invokeCallbacks(RenderSequenceImpl.RESOLVE_TASK_EXECUTION, { renderContext: taskRenderContext, taskId: taskId });
			taskInfo.taskExecution.resolve();
			this._currentRenderContext[taskInfo.task.id] = taskRenderContext;
		}

		/**
		 * @implements RenderSequenceInternalAPI#rejectTaskExecution
		 */
		rejectTaskExecution(taskInfo) {
			let taskRenderContext = this._createTaskRenderContext(taskInfo.renderContext);
			this.invokeCallbacks(RenderSequenceImpl.REJECT_TASK_EXECUTION, { error: taskInfo.error, renderContext: taskRenderContext, taskId: taskInfo.task.instance.getId() });
			taskInfo.taskExecution.reject(taskInfo.error);
			this._currentRenderContext[taskInfo.task.id] = taskRenderContext;
		}

		/**
		 * @implements RenderSequenceAPI#onStartTaskExecution
		 */
		onStartTaskExecution(method) {
			this.registerCallback(RenderSequenceImpl.START_TASK_EXECUTION, method);
		}

		/**
		 * @implements RenderSequenceAPI#onSuccessEndTaskExecution
		 */
		onResolveTaskExecution(method) {
			this.registerCallback(RenderSequenceImpl.RESOLVE_TASK_EXECUTION, method);
		}

		/**
		 * @implements RenderSequenceAPI#onFailureEndTaskExecution
		 */
		onRejectTaskExecution(method) {
			this.registerCallback(RenderSequenceImpl.REJECT_TASK_EXECUTION, method);
		}

		/**
		 * @implements RenderSequenceAPI#registerRenderStepProvider
		 */
		registerRenderStepProvider(provider){
			if (provider){
				if (typeof(provider.getRenderStepList) !== 'function') {
					throw new Error('the provide must implement "getRenderStepList()" ');
				}
				this._renderStepProviders.push(provider);
			}
		}

		/**
		 * @implements RenderSequenceAPI#getProvidersRenderStepList
		 */
		getProvidersRenderStepList() {
			var steps = [];
			this._renderStepProviders.forEach((provider) => {
				try {
					let extraSteps =provider.getRenderStepList();
					if(Array.isArray(extraSteps)) {
						steps.push.apply(steps, extraSteps);
					} else {
						this._logger.warn('Provide render steps are ignored. The render step provider did not return an array. ');
					}
				} catch (e){
					this._logger.error('An error while getting the render step list from the provider', e);
				}

			});
			return steps;
		}

		/**
		 * creates an instance of RenderContext with the API method of {@link RenderContextAPI}
		 * @param {Object} renderContext - sequence renderContext
		 */
		_createTaskRenderContext(renderContext) {
			let renderContextImpl = new RenderContextImpl({ logger: this._logger, renderContext:  renderContext });
			let api = APIFactory.createAPI(renderContextImpl, [RenderContextAPI]);
			return api;
		}

		/**
		 * Callback to task execution start
		 * @param {RenderContextAPI} payload
		 */
		s12yOnStartTaskExecution(payload) {
			let s12yInfo = this._s12yAPI.getContentInfo();
			if (!s12yInfo) {
				this._logger.info('Serviceability feature is not enabled');
			} else {
				let sequenceId = payload.renderContext.getSequenceId();
				let renderingInfo = s12yInfo.getData(RenderSequenceImpl.S12Y_RENDERING_INFO);
				let currentRender = renderingInfo.getCurrentRendering();
				if(currentRender) {
					currentRender.setWithData(true);
					let renderSequenceInfo = currentRender.getRenderSequenceWithData();
					if (this._isNewerSequence(sequenceId, renderSequenceInfo)) {
						renderSequenceInfo = new RenderSequenceInfo({ id: sequenceId });
						renderSequenceInfo.addTaskInfo({ taskId: payload.taskId });
						currentRender.setRenderSequenceWithData(renderSequenceInfo);
					}
				}
			}
		}

		/**
		 * Callback to task resolution
		 * TODO: introduce a generic way to get task providing their own result
		 * @param {RenderContextAPI} payload
		 */
		s12yOnResolveTaskExecution(payload) {
			let taskExecInfo = this._setTaskExecEndTime(payload);
			const data = payload.renderContext.getData(payload.taskId);
			if (!_.isEmpty(data)) {
				this._setQueryFeedbackSQLResult(data);
				this._setRenderInfoZIPIResult(data);
			}
			if (taskExecInfo) {
				this._setDataResult(taskExecInfo, payload.renderContext.getData(payload.taskId));
			}
		}

		/**
		 * Callback to task rejection
		 * TODO: remove the assumption the task is a data task
		 * @param {RenderContextAPI} payload
		 */
		s12yOnRejectTaskExecution(payload) {
			let taskExecInfo = this._setTaskExecEndTime(payload);
			if (taskExecInfo) {
				this._setTaskExecError(taskExecInfo, payload.error);
			}
		}

		_setQueryFeedbackSQLResult(data) {
			let contentInfo = this._s12yAPI.getContentInfo();
			if(contentInfo) {
				const sqlInfo = contentInfo.getData(RenderSequenceImpl.S12Y_SQL_INFO);
				data.getQueryResultIdList().forEach(id => {
					const queryResult = data.getResult(id);
					sqlInfo.widgetsLastCognosQuery = queryResult.getPropertyValue('QueryFeedback.cognosSQL');
					sqlInfo.widgetsLastNativeQuery = queryResult.getPropertyValue('QueryFeedback.nativeSQL');
					sqlInfo.widgetsLastMDXQuery = queryResult.getPropertyValue('QueryFeedback.MDX');
				});
			} else {
				this._logger.info('Serviceability feature is not enabled');
			}
		}

		_setRenderInfoZIPIResult(data) {
			const contentInfo = this._s12yAPI.getContentInfo();
			if(contentInfo) {
				const renderingInfo = contentInfo.getData(RenderSequenceImpl.S12Y_RENDERING_INFO);
				const zipiIds = [];
				data.getQueryResultIdList().forEach(id => {
					const queryResult = data.getResult(id);
					const zipiId = queryResult.getPropertyValue('ZIPI.id');
					if (zipiId) {
						zipiIds.push(zipiId);
					}
				});
				renderingInfo.setZipiIds(zipiIds);
			} else {
				this._logger.info('Serviceability feature is not enabled');
			}
		}

		_setSQLFetchParams(renderContext) {
			let contentInfo = this._s12yAPI.getContentInfo();
			if(contentInfo) {
				renderContext.extraInfo = renderContext.extraInfo || {};
				let sqlInfo = contentInfo.getData(RenderSequenceImpl.S12Y_SQL_INFO);
				if(sqlInfo.shouldFetchQuery) {
					const { dataQueryParams = {} } = renderContext.extraInfo;
					// TODO: Should move it to a render task provider
					// Avoid overwriting the existing qfb value
					const { qfb = '' } = dataQueryParams;
					const qfbList = qfb ? qfb.split(',') : [];
					const serviceabilityQfbList = ['nativeCommandText','nativeCommandType','CognosCommandText'];
					for(const option of serviceabilityQfbList) {
						qfbList.push(option);
					}
					renderContext.extraInfo.dataQueryParams = { qfb: qfbList.toString() };
				}
			} else {
				this._logger.info('Serviceability feature is not enabled');
			}
		}

		/**
		 * @private
		 * @param {integer} startedSequenceId
		 * @param {RenderSequenceInfo} s12ySequenceInfo
		 * @returns {boolean} true if s12ySequenceInfo is undefined or the startedSequenceId is greater than the s12ySequenceInfo one
		 */
		_isNewerSequence(startedSequenceId, s12ySequenceInfo) {
			return !s12ySequenceInfo || startedSequenceId > s12ySequenceInfo.getId();
		}

		/**
		 * sets the task end time if needed
		 * @private
		 * @param {RenderContextAPI} options - callback payload
		 * @returns {RenderTaskExecInfo} - the task exec info or undefined if none is to be updated
		 */
		_setTaskExecEndTime(options) {
			let taskExecInfo;
			let s12yInfo = this._s12yAPI.getContentInfo();
			if (!s12yInfo) {
				this._logger.info('Serviceability feature is not enabled');
			} else {
				let sequenceId = options.renderContext.getSequenceId();
				let s12yRenderingInfo = s12yInfo.getData(RenderSequenceImpl.S12Y_RENDERING_INFO);
				let currentRenderInfo = s12yRenderingInfo.getCurrentRendering();
				if (currentRenderInfo) {
					let renderSequenceInfo = currentRenderInfo.getRenderSequenceWithData();
					if (renderSequenceInfo && sequenceId === renderSequenceInfo.getId()) {
						taskExecInfo =  renderSequenceInfo.getTaskInfo(options.taskId);
						taskExecInfo.setEndTime();
					} else {
						this._logger.info(`s12y - setting end time - task [${options.taskId}] sequence ${sequenceId} from is ignored by the registered s12y sequence info`, renderSequenceInfo);
					}
				}
			}
			return taskExecInfo;
		}

		/**
		 * creates an error object and sets it in the taskExecInfo
		 * TODO: there should be only one logic to generate the error object; there is one in VisRenderSequence and one here
		 * @param {RenderTaskExecInfo} taskExecInfo
		 * @param {Object} error
		 */
		_setTaskExecError(taskExecInfo, error) {
			let msg = 'dwErrorRenderingVisualization';
			if (error) {
				if (error.msg) {
					msg = error.msg;
				} else if (error.message) {
					msg = error.message;
				}
			}
			const errorInfo = error ? error.errorInfo : undefined;
			taskExecInfo.setError(new APIError({ msg: msg, params: { errorInfo: errorInfo } }));
			if (errorInfo && typeof errorInfo.getResponseHeader === 'function') {
				taskExecInfo.addRequestTimeInfo(errorInfo.getResponseHeader('x-ca-requesttime'));
			}
		}

		/**
		 * set data result; create a data with a toJSON method to make sure to grab the values only when needed
		 * @param {RenderTaskExecInfo} taskExecInfo
		 * @param {QueryResultsAPI} data
		 */
		_setDataResult(taskExecInfo, data) {
			if (taskExecInfo && !_.isEmpty(data)) {
				data.getQueryResultIdList().forEach(id => {
					const queryResult = data.getResult(id);
					const requestTime = queryResult.getPropertyValue('RequestTime.internal');
					taskExecInfo.addRequestTimeInfo(requestTime);
				});

				let taskResults = {
					toJSON: () => {
						let result = [];
						data.getQueryResultIdList().forEach(id => {
							const queryResult = data.getResult(id);
							result.push({
								'spec': JSON.parse(queryResult.getPropertyValue('QuerySpec.internal')),
								'data': JSON.parse(queryResult.getPropertyValue('RawData.internal')),
								requestTime: queryResult.getPropertyValue('RequestTime.internal')
							});
						});
						return result;
					}
				};
				taskExecInfo.setData('queryResults', taskResults);
			}
		}
	}

	return RenderSequenceImpl;
});
